import React, { useEffect, useState } from 'react';
import './AdminUserDetail.css';
import './AdminFraud.css';
import { adminApi } from './utils/adminApi';
import { formatDate, formatDateTime, formatCurrency, toDatetimeLocalValue } from './utils/format';
import { countryCodeToFlagEmoji } from './utils/countryFlag';
import { STATUS_COLORS, PLAN_LABELS, VALID_BLOCKCHAINS, EDITABLE_FIELD_TYPES, SURVEY_SOURCES } from './constants';
import AdminGrantSubscriptionModal from './AdminGrantSubscriptionModal';
import RefreshButton from './RefreshButton';
import AccountGraph from './AccountGraph';

const planStatusLabel = (status) => {
  switch (status) {
    case 'active': return 'Active';
    case 'expired': return 'Expired';
    case 'inactive': return 'Inactive';
    default: return 'None';
  }
};

const FieldInput = ({ spec, value, onChange }) => {
  switch (spec.type) {
    case 'boolean':
      return (
        <label className="admin-inline-checkbox">
          <input type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} autoFocus />
          {value ? 'Yes' : 'No'}
        </label>
      );
    case 'enum':
      return (
        <select value={value ?? ''} onChange={(e) => onChange(e.target.value)} autoFocus>
          {spec.nullable && <option value="">—</option>}
          {spec.values.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      );
    case 'number':
      return (
        <input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          autoFocus
        />
      );
    case 'timestamp':
      return <input type="datetime-local" value={value ?? ''} onChange={(e) => onChange(e.target.value)} autoFocus />;
    case 'blockchainArray':
      return (
        <div className="admin-inline-checkbox-row">
          {VALID_BLOCKCHAINS.map((chain) => (
            <label key={chain} className="admin-inline-checkbox">
              <input
                type="checkbox"
                checked={(value || []).includes(chain)}
                onChange={(e) => {
                  const set = new Set(value || []);
                  if (e.target.checked) set.add(chain); else set.delete(chain);
                  onChange([...set]);
                }}
              />
              {chain}
            </label>
          ))}
        </div>
      );
    default:
      return (
        <input
          type={spec.type === 'email' ? 'email' : 'text'}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          autoFocus
        />
      );
  }
};

const Section = ({ title, children }) => (
  <div className="admin-detail-section">
    <h3>{title}</h3>
    {children}
  </div>
);

const formatDuration = (ms) => {
  if (!ms || ms <= 0) return '—';
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ${mins % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
};

const ROW = ({ label, value }) => (
  <div className="admin-detail-row">
    <span className="admin-detail-row-label">{label}</span>
    <span className="admin-detail-row-value">{value ?? '—'}</span>
  </div>
);

const sessionTimeAgo = (timestamp) => {
  if (!timestamp) return '—';
  const date = timestamp._seconds ? new Date(timestamp._seconds * 1000) : new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '—';
  const secs = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (secs < 120) return 'online now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

const FP_COMPONENT_LABELS = {
  screen: 'Screen',
  tz: 'Timezone',
  lang: 'Language',
  platform: 'Platform',
  cores: 'CPU cores',
  memory: 'RAM (GB)',
  touchPoints: 'Touch points',
  gpu: 'GPU',
  webglVendor: 'GPU vendor',
  webglRenderer: 'GPU renderer',
  webglExtCount: 'WebGL extensions',
  pixelStable: 'Pixel readback stable',
  canvasStable: 'Canvas stable',
};

const FP_GROUPS = [
  { type: 'primary', field: 'primaryFingerprints', label: 'Primary (device / WebGL)' },
  { type: 'secondary', field: 'secondaryFingerprints', label: 'Secondary (browser / canvas)' },
  // Accounts created/used before the primary/secondary split, or before the
  // (now-removed) audio layer was taken out, only have this flat array —
  // keep showing it so those accounts don't look empty.
  { type: 'legacy', field: 'fingerprints', label: 'Legacy (single fingerprint, pre-upgrade)' },
];

// Read-only behavior-tracking summary, sessions (devices the account is used
// from), recent events timeline, and fingerprint detail with clickable
// matched accounts. Data comes from user.tracking + the activity endpoints.
const ActivitySection = ({ user, onSelectUser, onRefreshUser }) => {
  const [events, setEvents] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [sessions, setSessions] = useState(null);
  const [fpDetails, setFpDetails] = useState({});

  const tracking = user.tracking || {};
  const attribution = tracking.attribution || null;
  const counts = tracking.eventCounts || {};
  const [unmergingHash, setUnmergingHash] = useState(null);
  const [relationships, setRelationships] = useState(undefined); // undefined = not loaded yet

  const loadRelationships = async () => {
    setRelationships('loading');
    try {
      const res = await adminApi.getAccountGraph(user.id);
      setRelationships(res.found ? res.group : null);
    } catch (err) {
      console.error('Failed to load relationships:', err);
      setRelationships(null);
    }
  };

  const loadSessions = async () => {
    const res = await adminApi.getUserSessions(user.id);
    setSessions(res.sessions || []);
  };

  useEffect(() => {
    let cancelled = false;
    setSessions(null);
    setEvents(null);
    setFpDetails({});
    adminApi.getUserSessions(user.id)
      .then((res) => { if (!cancelled) setSessions(res.sessions || []); })
      .catch((err) => {
        console.error('Failed to load sessions:', err);
        if (!cancelled) setSessions([]);
      });
    return () => { cancelled = true; };
  }, [user.id]);

  const loadEvents = async () => {
    setEventsLoading(true);
    try {
      const res = await adminApi.getActivityEvents({ uid: user.id, limit: 25 });
      setEvents(res.events || []);
    } catch (err) {
      console.error('Failed to load user events:', err);
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  // A session is "active" while it hasn't been logged out and was seen
  // within the 7-day login-cookie lifetime.
  const isActiveSession = (s) => {
    if (s.endedAt) return false;
    const ts = s.lastSeenAt && (s.lastSeenAt._seconds ? s.lastSeenAt._seconds * 1000 : new Date(s.lastSeenAt).getTime());
    return Boolean(ts) && Date.now() - ts < 7 * 24 * 60 * 60 * 1000;
  };
  const activeSessions = (sessions || []).filter(isActiveSession);
  const expiredSessions = (sessions || []).filter((s) => !isActiveSession(s));

  const renderSessionCard = (s, expired) => (
    <div key={s.id} className={`admin-session-card ${expired ? 'admin-session-expired' : ''}`}>
      <div className="admin-session-main">
        <span className="admin-session-device">
          {[s.device?.browser, s.device?.os].filter(Boolean).join(' · ') || 'Unknown device'}
          {s.device?.deviceType && s.device.deviceType !== 'desktop' && (
            <span className="admin-session-type"> ({s.device.deviceType})</span>
          )}
        </span>
        <span className={`admin-session-seen ${!expired && sessionTimeAgo(s.lastSeenAt) === 'online now' ? 'admin-session-online' : ''}`}>
          {s.endedAt ? `logged out ${sessionTimeAgo(s.endedAt)}` : sessionTimeAgo(s.lastSeenAt)}
        </span>
      </div>
      <div className="admin-session-sub">
        <span>
          {[s.geo?.city, s.geo?.country].filter(Boolean).join(', ') || '—'}
          {s.geo?.ip && <span className="admin-mono"> · {s.geo.ip}</span>}
        </span>
        <span>started {formatDateTime(s.startedAt)}</span>
      </div>
    </div>
  );

  const loadFingerprint = async (hash) => {
    setFpDetails((prev) => ({ ...prev, [hash]: 'loading' }));
    try {
      const res = await adminApi.getFingerprintMatches(hash);
      setFpDetails((prev) => ({ ...prev, [hash]: res }));
    } catch (err) {
      console.error('Fingerprint lookup failed:', err);
      setFpDetails((prev) => ({ ...prev, [hash]: { users: [], visitorIds: [] } }));
    }
  };

  const unmergeFp = async (hash, type) => {
    if (!window.confirm('Unmerge this fingerprint from this account? It will no longer be linked here (the fraud registry keeps its history).')) return;
    setUnmergingHash(hash);
    try {
      await adminApi.unmergeFingerprint({ hash, uid: user.id, type });
      onRefreshUser && onRefreshUser();
    } catch (err) {
      console.error('Unmerge failed:', err);
      alert(err.message || 'Unmerge failed');
    } finally {
      setUnmergingHash(null);
    }
  };

  return (
    <Section title="Activity">
      <ROW label="Last seen" value={user.lastActivity ? formatDateTime(user.lastActivity) : '—'} />
      <ROW label="Last page" value={user.lastPage ? <span className="admin-mono">{user.lastPage}</span> : '—'} />
      <ROW label="Sessions" value={tracking.sessionCount ?? 0} />
      <ROW label="Page views" value={tracking.pageViews ?? 0} />
      <ROW label="Time active" value={formatDuration(tracking.totalActiveMs)} />
      <ROW label="Errors hit" value={tracking.errorCount ?? 0} />
      <ROW label="Downloads" value={counts.download_link_generated ?? 0} />
      <ROW label="Checkout attempts" value={counts.checkout_submitted ?? 0} />

      {attribution && (
        <>
          <ROW label="First landing page" value={attribution.firstLandingPage} />
          <ROW
            label="First referrer"
            value={attribution.firstReferrer ? <span className="admin-mono admin-wrap">{attribution.firstReferrer}</span> : '—'}
          />
          {attribution.utmSource && <ROW label="UTM source" value={attribution.utmSource} />}
          {attribution.utmCampaign && <ROW label="UTM campaign" value={attribution.utmCampaign} />}
          <ROW label="Arrived via ref" value={attribution.ref} />
        </>
      )}

      <div className="admin-sessions-block">
        <div className="admin-block-head">
          <div className="admin-subblock-title">Active sessions</div>
          <RefreshButton onRefresh={loadSessions} title="Refresh sessions" />
        </div>
        {sessions === null ? (
          <div className="no-data">Loading sessions…</div>
        ) : activeSessions.length === 0 ? (
          <div className="no-data">Not logged in anywhere right now</div>
        ) : (
          <div className="admin-sessions-list">
            {activeSessions.map((s) => renderSessionCard(s, false))}
          </div>
        )}

        {sessions !== null && expiredSessions.length > 0 && (
          <>
            <div className="admin-subblock-title admin-subblock-expired">Expired / logged-out sessions</div>
            <div className="admin-sessions-list">
              {expiredSessions.map((s) => renderSessionCard(s, true))}
            </div>
          </>
        )}
      </div>

      {FP_GROUPS.map(({ type, field, label }) => {
        const hashes = tracking[field] || [];
        if (hashes.length === 0) return null;
        return (
          <div className="admin-sessions-block" key={type}>
            <div className="admin-subblock-title">{label}</div>
          {hashes.map((fp) => {
            const detail = fpDetails[fp];
            const others = detail && detail !== 'loading'
              ? (detail.users || []).filter((u) => u.id !== user.id)
              : [];
            return (
              <div key={fp} className="admin-fp-card">
                <div className="admin-fp-card-head">
                  <span className="admin-mono">{fp.slice(0, 16)}…</span>
                  <span className="admin-fp-card-actions">
                    {detail === undefined && (
                      <button className="admin-fp-check" onClick={() => loadFingerprint(fp)}>show info & matches</button>
                    )}
                    {detail === 'loading' && <span className="admin-fp-ok">loading…</span>}
                    <button
                      className="admin-fp-check admin-fp-unmerge"
                      disabled={unmergingHash === fp}
                      onClick={() => unmergeFp(fp, type)}
                      title="Remove this fingerprint from this account"
                    >
                      {unmergingHash === fp ? 'removing…' : 'unmerge'}
                    </button>
                  </span>
                </div>
                {detail && detail !== 'loading' && (
                  <div className="admin-fp-card-body">
                    {others.length === 0 ? (
                      <div className="admin-fp-ok">No other accounts with this fingerprint</div>
                    ) : (
                      <div className="admin-fp-warn">
                        Also used by:{' '}
                        {others.map((m, i) => (
                          <React.Fragment key={m.id}>
                            {i > 0 && ', '}
                            <button
                              className="activity-user-link"
                              onClick={() => onSelectUser && onSelectUser(m.id)}
                            >
                              {m.email || m.id}
                            </button>
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                    {(detail.visitorIds || []).length > 0 && (
                      <div className="admin-fp-meta">{detail.visitorIds.length} anonymous visitor(s) seen with it</div>
                    )}
                    {detail.components && (
                      <div className="admin-fp-components">
                        {Object.entries(FP_COMPONENT_LABELS).map(([key, compLabel]) =>
                          detail.components[key] !== undefined ? (
                            <div key={key} className="admin-fp-component">
                              <span>{compLabel}</span>
                              <span className="admin-mono">
                                {typeof detail.components[key] === 'boolean'
                                  ? (detail.components[key] ? 'yes' : 'no')
                                  : String(detail.components[key])}
                              </span>
                            </div>
                          ) : null
                        )}
                        {detail.device && (
                          <div className="admin-fp-component">
                            <span>Browser / OS</span>
                            <span className="admin-mono">
                              {[detail.device.browser, detail.device.os].filter(Boolean).join(' / ') || '—'}
                            </span>
                          </div>
                        )}
                        {detail.lastIp && (
                          <div className="admin-fp-component">
                            <span>Last IP</span>
                            <span className="admin-mono">{detail.lastIp}</span>
                          </div>
                        )}
                        <div className="admin-fp-component">
                          <span>First / last seen</span>
                          <span>{formatDate(detail.firstSeenAt)} → {formatDate(detail.lastSeenAt)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          </div>
        );
      })}

      <div className="admin-sessions-block">
        <div className="admin-block-head">
          <div className="admin-subblock-title">Relationships (linked accounts, any device)</div>
          {relationships !== undefined && relationships !== 'loading' && (
            <RefreshButton onRefresh={loadRelationships} title="Refresh relationships" />
          )}
        </div>
        {relationships === undefined && (
          <button className="admin-btn admin-btn-secondary" onClick={loadRelationships}>
            Check for linked accounts
          </button>
        )}
        {relationships === 'loading' && <div className="no-data">Loading…</div>}
        {relationships === null && <div className="no-data">No other accounts share a fingerprint with this one</div>}
        {relationships && relationships !== 'loading' && (
          <>
            <div className="admin-fp-meta">
              Connected to {relationships.accounts.length - 1} other account(s) through {relationships.links.length} fingerprint link(s).
            </div>
            <div className="fraud-graph-wrap">
              <AccountGraph accounts={relationships.accounts} links={relationships.links} onSelectUser={onSelectUser} focusUid={user.id} size={Math.min(420, 180 + relationships.accounts.length * 45)} />
            </div>
          </>
        )}
      </div>

      <div className="admin-events-block">
        {events === null ? (
          <button className="admin-btn admin-btn-secondary" disabled={eventsLoading} onClick={loadEvents}>
            {eventsLoading ? 'Loading…' : 'Load recent events'}
          </button>
        ) : (
          <>
            <div className="admin-block-head">
              <div className="admin-subblock-title">Recent events</div>
              <RefreshButton onRefresh={loadEvents} title="Refresh events" />
            </div>
            {events.length === 0 ? (
              <div className="no-data">No events in the last 90 days</div>
            ) : (
              <div className="admin-events-list">
                {events.map((e) => (
                  <div key={e.id} className="admin-event-row">
                    <span className="admin-event-time">{formatDateTime(e.ts)}</span>
                    <span className="admin-event-type">{e.type}</span>
                    <span className="admin-event-page admin-mono">{e.page}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Section>
  );
};

const EditableRow = ({ label, path, displayValue, seedValue, editingEnabled, openFields, pendingValues, onOpen, onChange }) => {
  const isEditable = editingEnabled && Boolean(path);
  const isOpen = path && openFields.has(path);

  if (!isOpen) {
    return (
      <div
        className={`admin-detail-row ${isEditable ? 'admin-row-editable' : ''}`}
        onClick={isEditable ? () => onOpen(path, seedValue) : undefined}
      >
        <span className="admin-detail-row-label">{label}</span>
        <span className="admin-detail-row-value">{displayValue ?? '—'}</span>
      </div>
    );
  }

  const spec = EDITABLE_FIELD_TYPES[path];
  const value = pendingValues[path] !== undefined ? pendingValues[path] : seedValue;

  return (
    <div className="admin-detail-row admin-detail-row-editing">
      <span className="admin-detail-row-label">{label}</span>
      <FieldInput spec={spec} value={value} onChange={(v) => onChange(path, v)} />
    </div>
  );
};

const AdminUserDetail = ({ uid, onClose, onChanged, onSelectUser }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [fullBanResult, setFullBanResult] = useState(null);

  const [editingEnabled, setEditingEnabled] = useState(false);
  const [openFields, setOpenFields] = useState(new Set());
  const [pendingValues, setPendingValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // soft=true refreshes in place without blanking the panel to "Loading..."
  const load = async (soft = false) => {
    if (!soft) setLoading(true);
    setError('');
    try {
      const res = await adminApi.getUser(uid);
      setUser(res.user);
    } catch (err) {
      console.error('Error loading user detail:', err);
      setError(err.message || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (uid) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const runAction = async (fn) => {
    setActionLoading(true);
    setActionError('');
    try {
      await fn();
      await load();
      onChanged?.();
    } catch (err) {
      console.error('Admin action failed:', err);
      setActionError(err.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveSubscription = () => {
    if (!window.confirm('Remove this user\'s subscription? This resets their plan to inactive.')) return;
    runAction(() => adminApi.removeSubscription(uid));
  };

  const handleBanToggle = () => {
    if (user.accountBanned) {
      runAction(() => adminApi.unbanUser(uid));
      return;
    }
    // Reason is optional but, when given, shown to the user directly on
    // their full-screen "account banned" modal — keep the prompt visible
    // rather than folding it into window.confirm so it's clearly separate.
    const reason = window.prompt(
      'Ban this user? This blocks all of their access immediately.\n\nOptional reason (shown to the user):'
    );
    if (reason === null) return; // cancelled
    runAction(() => adminApi.banUser(uid, reason));
  };

  const handleSuspendToggle = () => {
    runAction(() => (user.accountSuspended ? adminApi.unsuspendUser(uid) : adminApi.suspendUser(uid)));
  };

  // Full ban/unban is a bulk, cascading action: it bans (or unbans) not just
  // this account but EVERY account currently connected to it through a
  // shared primary (device-bound) fingerprint, and flags (or clears) every
  // device (primary + secondary) touched anywhere in that group. This is
  // deliberately separate from the plain Ban/Unban button below, which only
  // ever touches this one account and never looks at devices — that stays
  // available even while a device is banned, e.g. to release someone who
  // shares a device with a banned account but isn't actually the same
  // person. Note that if they log in again from a device that's still
  // flagged, the propagation check (see email_auth/login_auth.js) will
  // auto-suspend them again — the device flag is the more durable signal,
  // individual unban is an override, not a fix.
  const handleFullBan = async () => {
    const willBan = !user.accountBanned;
    let reason;
    if (willBan) {
      reason = window.prompt(
        "Full Ban: bans this account AND every account sharing a device with it, and flags every " +
        "device (primary + secondary) touched by the group. Future signups from those devices are " +
        "rejected outright, and any account — old or new — that logs in from one gets auto-suspended. " +
        "This cascades immediately, nothing is held for manual review.\n\n" +
        "Optional reason (shown to every account in the group):"
      );
      if (reason === null) return; // cancelled
    } else {
      if (!window.confirm(
        "Undo Full Ban: unbans this account AND every account currently sharing a device with it, and " +
        "clears the device-banned flag on every device touched by the group."
      )) return;
    }
    setActionLoading(true);
    setActionError('');
    setFullBanResult(null);
    try {
      const res = willBan ? await adminApi.fullBanUser(uid, reason) : await adminApi.fullUnbanUser(uid);
      setFullBanResult({ ...res, willBan });
      await load();
      onChanged?.();
    } catch (err) {
      console.error('Full ban/unban failed:', err);
      setActionError(err.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const openField = (path, seedValue) => {
    setOpenFields((prev) => new Set(prev).add(path));
    setPendingValues((prev) => (prev[path] !== undefined ? prev : { ...prev, [path]: seedValue }));
  };

  const changeField = (path, value) => {
    setPendingValues((prev) => ({ ...prev, [path]: value }));
  };

  const toggleEditingMode = () => {
    if (editingEnabled && Object.keys(pendingValues).length > 0) {
      if (!window.confirm('Discard unsaved edits?')) return;
    }
    setOpenFields(new Set());
    setPendingValues({});
    setSaveError('');
    setEditingEnabled((prev) => !prev);
  };

  const discardEdits = () => {
    setOpenFields(new Set());
    setPendingValues({});
    setSaveError('');
  };

  const saveEdits = async () => {
    if (Object.keys(pendingValues).length === 0) return;
    setSaving(true);
    setSaveError('');
    try {
      await adminApi.updateUserFields(uid, pendingValues);
      setOpenFields(new Set());
      setPendingValues({});
      await load();
      onChanged?.();
    } catch (err) {
      console.error('Error saving edits:', err);
      setSaveError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const rowProps = { editingEnabled, openFields, pendingValues, onOpen: openField, onChange: changeField };

  return (
    <>
      <div className="admin-detail-overlay" onClick={onClose} />
      <div className="admin-detail-panel open">
        <div className="admin-detail-header">
          <div className="admin-detail-title">{loading ? 'Loading…' : (user?.email || 'User')}</div>
          <div className="admin-detail-header-actions">
            {!loading && user && (
              <>
                <RefreshButton onRefresh={() => load(true)} title="Refresh user info" />
                <button
                  className={`admin-btn ${editingEnabled ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
                  onClick={toggleEditingMode}
                >
                  {editingEnabled ? 'Done Editing' : 'Enable Editing'}
                </button>
              </>
            )}
            <button className="admin-modal-close" onClick={onClose}>×</button>
          </div>
        </div>

        {loading && <div className="admin-loading">Loading...</div>}
        {error && <div className="admin-error">{error}</div>}

        {user && !loading && (
          <div className="admin-detail-body">
            <div className="admin-row-badges">
              <span className="admin-badge" style={{ color: STATUS_COLORS[user.planStatus] }}>
                {planStatusLabel(user.planStatus)}
              </span>
              {user.admin && <span className="admin-badge" style={{ color: STATUS_COLORS.admin }}>Admin</span>}
              {user.accountBanned && <span className="admin-badge" style={{ color: STATUS_COLORS.banned }}>Banned</span>}
              {user.accountSuspended && <span className="admin-badge" style={{ color: STATUS_COLORS.suspended }}>Suspended</span>}
            </div>

            {editingEnabled && (
              <div className="admin-edit-hint">Editing enabled — click any value below to change it, then Save.</div>
            )}
            {actionError && <div className="admin-error">{actionError}</div>}

            <Section title="Identity">
              <EditableRow label="Email" path="email" displayValue={user.email} seedValue={user.email || ''} {...rowProps} />
              <EditableRow label="User ID" path={null} displayValue={<span className="admin-mono">{user.id}</span>} {...rowProps} />
              <EditableRow
                label="Signed up"
                path="createdAt"
                displayValue={formatDate(user.createdAt)}
                seedValue={toDatetimeLocalValue(user.createdAt)}
                {...rowProps}
              />
              <EditableRow
                label="Last login"
                path="lastLogin"
                displayValue={formatDateTime(user.lastLogin)}
                seedValue={toDatetimeLocalValue(user.lastLogin)}
                {...rowProps}
              />
              <EditableRow
                label="Login count"
                path="loginCount"
                displayValue={user.loginCount ?? 0}
                seedValue={user.loginCount ?? 0}
                {...rowProps}
              />
              <EditableRow
                label="Telegram"
                path="telegramUsername"
                displayValue={user.telegramUsername}
                seedValue={user.telegramUsername || ''}
                {...rowProps}
              />
              <EditableRow label="Email verified" path={null} displayValue={user.emailVerified ? 'Yes' : 'No'} {...rowProps} />
              <EditableRow label="Password updated" path={null} displayValue={formatDateTime(user.passwordUpdatedAt)} {...rowProps} />
              <EditableRow label="Last modified" path={null} displayValue={formatDateTime(user.lastUpdated)} {...rowProps} />
            </Section>

            <Section title="Subscription">
              <EditableRow
                label="Status"
                path="status"
                displayValue={user.status}
                seedValue={user.status || 'inactive'}
                {...rowProps}
              />
              <EditableRow
                label="Plan"
                path="planId"
                displayValue={user.planId ? (PLAN_LABELS[user.planId] || user.planId) : 'None'}
                seedValue={user.planId || ''}
                {...rowProps}
              />
              <EditableRow
                label="Blockchains"
                path="blockchains"
                displayValue={user.blockchains?.length ? user.blockchains.join(', ') : '—'}
                seedValue={user.blockchains || []}
                {...rowProps}
              />
              <EditableRow
                label="Expires"
                path="plan_expires"
                displayValue={user.plan_expires ? formatDate(user.plan_expires) : (user.planId ? 'Lifetime' : '—')}
                seedValue={toDatetimeLocalValue(user.plan_expires)}
                {...rowProps}
              />
              <EditableRow
                label="Granted manually"
                path={null}
                displayValue={user.grantedManually ? `Yes (by ${user.grantedByAdmin || 'admin'})` : 'No'}
                {...rowProps}
              />
              <EditableRow label="Purchased at" path={null} displayValue={formatDateTime(user.purchased_at)} {...rowProps} />
              <EditableRow label="Payment status" path={null} displayValue={user.paymentStatus || '—'} {...rowProps} />
            </Section>

            {[
              { source: 'signupInfo', title: 'Signup — location & device' },
              { source: 'lastLoginInfo', title: 'Last login — location & device' },
            ].map(({ source, title }) => {
              const info = user[source] || {};
              return (
                <Section title={title} key={source}>
                  {source === 'lastLoginInfo' && (
                    <EditableRow label="At" path={null} displayValue={formatDateTime(info.at)} {...rowProps} />
                  )}
                  <EditableRow
                    label="Country"
                    path={`${source}.country`}
                    displayValue={info.countryCode ? `${countryCodeToFlagEmoji(info.countryCode)} ${info.country}` : (info.country || '—')}
                    seedValue={info.country || ''}
                    {...rowProps}
                  />
                  <EditableRow
                    label="Country code"
                    path={`${source}.countryCode`}
                    displayValue={info.countryCode}
                    seedValue={info.countryCode || ''}
                    {...rowProps}
                  />
                  <EditableRow
                    label="Region"
                    path={`${source}.region`}
                    displayValue={info.region}
                    seedValue={info.region || ''}
                    {...rowProps}
                  />
                  <EditableRow
                    label="City"
                    path={`${source}.city`}
                    displayValue={info.city}
                    seedValue={info.city || ''}
                    {...rowProps}
                  />
                  <EditableRow
                    label="IP"
                    path={`${source}.ip`}
                    displayValue={<span className="admin-mono">{info.ip || '—'}</span>}
                    seedValue={info.ip || ''}
                    {...rowProps}
                  />
                  <EditableRow
                    label="Browser"
                    path={`${source}.browser`}
                    displayValue={[info.browser, info.browserVersion].filter(Boolean).join(' ')}
                    seedValue={info.browser || ''}
                    {...rowProps}
                  />
                  <EditableRow
                    label="OS"
                    path={`${source}.os`}
                    displayValue={[info.os, info.osVersion].filter(Boolean).join(' ')}
                    seedValue={info.os || ''}
                    {...rowProps}
                  />
                  <EditableRow
                    label="Device type"
                    path={`${source}.deviceType`}
                    displayValue={info.deviceType}
                    seedValue={info.deviceType || ''}
                    {...rowProps}
                  />
                </Section>
              );
            })}

            <Section title="Referral">
              <EditableRow label="Referral code" path="refId" displayValue={user.refId} seedValue={user.refId || ''} {...rowProps} />
              <EditableRow
                label="Referred by"
                path="referredBy"
                displayValue={user.referredBy || '—'}
                seedValue={user.referredBy || ''}
                {...rowProps}
              />
              <EditableRow
                label="Signed up via ref"
                path={null}
                displayValue={user.signupRef || '—'}
                {...rowProps}
              />
              <EditableRow
                label="Commission earned"
                path="refCommission"
                displayValue={user.refCommission ? formatCurrency(user.refCommission) : '—'}
                seedValue={user.refCommission || 0}
                {...rowProps}
              />
              <EditableRow label="Referred at" path={null} displayValue={formatDateTime(user.referredAt)} {...rowProps} />
              <EditableRow
                label="Withdraw pending"
                path={null}
                displayValue={typeof user.withdrawPending === 'number' ? formatCurrency(user.withdrawPending) : '—'}
                {...rowProps}
              />
              <EditableRow
                label="Withdraw completed"
                path={null}
                displayValue={typeof user.withdrawComplete === 'number' ? formatCurrency(user.withdrawComplete) : '—'}
                {...rowProps}
              />
            </Section>

            <Section title="Survey">
              <EditableRow
                label="Completed"
                path="survey.completed"
                displayValue={user.survey?.completed ? 'Yes' : 'No'}
                seedValue={Boolean(user.survey?.completed)}
                {...rowProps}
              />
              <EditableRow
                label="Source"
                path="survey.source"
                displayValue={user.survey?.source}
                seedValue={SURVEY_SOURCES.includes(user.survey?.source) ? user.survey.source : ''}
                {...rowProps}
              />
              <EditableRow
                label="Other text"
                path="survey.otherText"
                displayValue={user.survey?.otherText || '—'}
                seedValue={user.survey?.otherText || ''}
                {...rowProps}
              />
            </Section>

            <ActivitySection user={user} onSelectUser={onSelectUser} onRefreshUser={() => load(true)} />

            <Section title="Quick actions">
              <div className="admin-actions-grid">
                <button className="admin-btn admin-btn-primary" disabled={actionLoading} onClick={() => setShowGrantModal(true)}>
                  Grant Subscription
                </button>
                <button className="admin-btn admin-btn-secondary" disabled={actionLoading || !user.planId} onClick={handleRemoveSubscription}>
                  Remove Subscription
                </button>
                <button className="admin-btn admin-btn-secondary" disabled={actionLoading} onClick={handleSuspendToggle}>
                  {user.accountSuspended ? 'Unsuspend' : 'Suspend'}
                </button>
                <button className="admin-btn admin-btn-danger" disabled={actionLoading} onClick={handleBanToggle} title="Ban or unban only this one account — never touches device flags">
                  {user.accountBanned ? 'Unban' : 'Ban'}
                </button>
                <button
                  className={`admin-btn ${user.accountBanned ? 'admin-btn-secondary' : 'admin-btn-danger'}`}
                  disabled={actionLoading}
                  onClick={handleFullBan}
                  title={user.accountBanned
                    ? 'Unban this account and every account sharing a device with it, and clear those devices\' banned flag'
                    : 'Ban this account and every account sharing a device with it, and flag those devices — future signups and other logins from them are blocked too'}
                >
                  {user.accountBanned ? 'Undo Full Ban (account + device)' : 'Full Ban (account + device)'}
                </button>
              </div>
              {fullBanResult && (
                <div className="fraud-hint" style={{ marginTop: 10 }}>
                  {fullBanResult.willBan ? (
                    <>
                      Banned {fullBanResult.accountsBanned} account(s) and flagged {fullBanResult.devicesBanned} device(s).
                      {fullBanResult.bannedAccounts.length > 1 && (
                        <>
                          {' '}Accounts:{' '}
                          {fullBanResult.bannedAccounts.map((a, i) => (
                            <React.Fragment key={a.id}>
                              {i > 0 && ', '}
                              <button className="activity-user-link" onClick={() => onSelectUser && onSelectUser(a.id)}>
                                {a.email || a.id}
                              </button>
                            </React.Fragment>
                          ))}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      Unbanned {fullBanResult.accountsUnbanned} account(s) and cleared {fullBanResult.devicesUnbanned} device(s).
                      {fullBanResult.unbannedAccounts.length > 1 && (
                        <>
                          {' '}Accounts:{' '}
                          {fullBanResult.unbannedAccounts.map((a, i) => (
                            <React.Fragment key={a.id}>
                              {i > 0 && ', '}
                              <button className="activity-user-link" onClick={() => onSelectUser && onSelectUser(a.id)}>
                                {a.email || a.id}
                              </button>
                            </React.Fragment>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </Section>
          </div>
        )}

        {editingEnabled && Object.keys(pendingValues).length > 0 && (
          <div className="admin-save-bar">
            {saveError && <div className="admin-field-error">{saveError}</div>}
            <div className="admin-save-bar-actions">
              <button className="admin-btn admin-btn-secondary" disabled={saving} onClick={discardEdits}>Discard</button>
              <button className="admin-btn admin-btn-primary" disabled={saving} onClick={saveEdits}>
                {saving ? 'Saving…' : `Save Changes (${Object.keys(pendingValues).length})`}
              </button>
            </div>
          </div>
        )}
      </div>

      {showGrantModal && (
        <AdminGrantSubscriptionModal
          uid={uid}
          currentPlanId={user?.planId}
          onClose={() => setShowGrantModal(false)}
          onGranted={() => {
            setShowGrantModal(false);
            load();
            onChanged?.();
          }}
        />
      )}
    </>
  );
};

export default AdminUserDetail;
